import type { NextPage } from "next";
import useTranslation from "next-translate/useTranslation";
import { THEMES } from "@/constants/themes.constant";
import { useColorSchema } from "@/context/ColorSchema";
import { LargeToggleButton } from "@/components/large-toggle-button/LargeToggleButton";
import { TickIcon } from "@/components/settings/tick-icon/TickIcon";
import { EmptyTickIcon } from "@/components/settings/empty-tick-icon/EmptyTickIcon";
import styles from "@/styles/pages/payments/profile/Settings.module.scss";
import {
  ENVIRONMENT,
  envBasedConfig,
} from "@/constants/env-based-config.constant";
import type { SupportedTextDirections } from "@/constants/languages.constant";
import { SUPPORTED_LANGUAGES } from "@/constants/languages.constant";
import { LargeToggleButtonLink } from "@/components/large-toggle-button/LargeToggleButtonLink";
import { HorizontalSeparator } from "@/xpa-components/horizontal-separator/HorizontalSeparator";
import classnames from "classnames";
import { ProfileHeaderUnauthenticated } from "@/components/profile/profile-header/ProfileHeaderUnauthenticated";
import { useAuthentication } from "@/context/Authentication";
import { ProfileHeaderAuthenticated } from "@/components/profile/profile-header/ProfileHeaderAuthenticated";
import { PageTabs } from "@/xpa-components/page-tabs/PageTabs";
import Head from "next/head";
import { useReducer } from "react";
import { useProfileTabs } from "@/utils/hooks/useProfileTabs";
import { StreamName, useRegisterStream } from "@/modules/analytics";
import { PROFILE_SETTINGS_ROUTE } from "@/constants/routes.constant";
import { OGImage } from "@/components/og-image/og-image";
import Link from "next/link";

const SettingsPage: NextPage = () => {
  const { t, lang } = useTranslation("web-payments");
  const { uiMode, setUiMode } = useColorSchema();
  const { authState: auth } = useAuthentication();
  const [areDebugLinksVisible, hideDebugLinks] = useReducer(() => false, true);
  const pageTabs = useProfileTabs(t`SETTINGS`);

  const mutablePageTabs = [...pageTabs];

  useRegisterStream(StreamName.ProfileSettings);
  const shouldHaveArabicLanguage = envBasedConfig.featureFlags.arabicLanguage;

  function handleChangeTextDirection(direction: SupportedTextDirections) {
    document.body.style.direction = direction;
  }

  return (
    <>
      <Head>
        <title>{`${t`SETTINGS`} | OneFootball`}</title>
        <OGImage />
      </Head>

      {ENVIRONMENT === "staging" && areDebugLinksVisible && (
        <div className={styles.debugLinks}>
          <Link href="/en/_debug/layout-tester">layout-tester</Link>
          <Link href="/en/_debug/menu">debug menu</Link>
          <button onClick={hideDebugLinks}>close</button>
        </div>
      )}

      <div className={styles.wrapper}>
        <h1 className="screen-reader-only">{t`SETTINGS`}</h1>

        {auth.kind === "unauthenticated" && <ProfileHeaderUnauthenticated />}

        {auth.kind === "authenticated" && <ProfileHeaderAuthenticated />}

        <PageTabs title={t`PROFILE_NAVIGATION`} items={mutablePageTabs} />

        <article className={styles.article}>
          <header className={styles.headerText}>
            <span>{t`SETTINGS_LANGUAGE`}</span>
          </header>

          <ul className={styles.controlsGrid}>
            {SUPPORTED_LANGUAGES.map(({ name, locale, textDirection }) => ({
              locale,
              name,
              textDirection,
              isActive: locale === lang,
              link: PROFILE_SETTINGS_ROUTE,
            }))
              .sort((prev, next) => (prev.name > next.name ? 1 : -1))
              .map(({ locale, name, isActive, link, textDirection }) => {
                if (!shouldHaveArabicLanguage && locale === "ar-SA") {
                  return;
                }
                const content = (
                  <span className={styles.controlContent}>
                    <span
                      className={classnames(styles.controlContentText, {
                        [String(styles.controlContentTextActive)]: isActive,
                        [String(styles.controlContentTextInactive)]: !isActive,
                      })}
                      lang={locale}
                    >
                      {name}
                    </span>

                    {isActive ? <TickIcon /> : <EmptyTickIcon />}
                  </span>
                );
                return (
                  <li
                    key={locale}
                    onClick={() => {
                      handleChangeTextDirection(textDirection);
                    }}
                  >
                    {isActive ? (
                      <LargeToggleButton
                        className={styles.control}
                        disabled={true}
                        isActive={true}
                      >
                        {content}
                      </LargeToggleButton>
                    ) : (
                      <LargeToggleButtonLink
                        className={styles.control}
                        href={link}
                        locale={locale}
                      >
                        {content}
                      </LargeToggleButtonLink>
                    )}
                  </li>
                );
              })}
          </ul>
        </article>

        <div className={styles.separator}>
          <HorizontalSeparator />
        </div>

        <article className={styles.article}>
          <header>
            <span
              className={styles.headerText}
            >{t`SETTINGS_DISPLAY_MODE`}</span>
          </header>

          <ul className={styles.controlsGrid}>
            {THEMES.map((mode) => {
              const isActive = uiMode === mode;

              return (
                <li key={mode}>
                  <LargeToggleButton
                    className={styles.control}
                    disabled={isActive}
                    isActive={isActive}
                    onClick={() => setUiMode(mode)}
                  >
                    <span className={styles.controlContent}>
                      <span
                        className={classnames(styles.controlContentText, {
                          [String(styles.controlContentTextActive)]: isActive,
                          [String(styles.controlContentTextInactive)]:
                            !isActive,
                        })}
                      >
                        {mode === "auto" && t`SETTINGS_UI_MODE_AUTO`}
                        {mode === "enforced-dark" && t`SETTINGS_UI_MODE_DARK`}
                        {mode === "enforced-light" && t`SETTINGS_UI_MODE_LIGHT`}
                      </span>

                      {isActive ? <TickIcon /> : <EmptyTickIcon />}
                    </span>
                  </LargeToggleButton>
                </li>
              );
            })}
          </ul>

          <p className={styles.controlGridDescription}>
            <span className={styles.controlGridDescriptionText}>
              {t`SETTINGS_DISPLAY_FOOTER`}
            </span>
          </p>
        </article>
      </div>
    </>
  );
};

export default SettingsPage;
